import withPageAuthRequired from "auth/withPageAuthRequired";
import JobsTable from "components/JobsTable";
import Layout from "components/Layout";
import ModalProvider from "components/ModalProvider";
import { useCurrentQueryParamStateWithDefaults } from "components/Table/hooks/useQueryParamState";
import { gql } from "generated/graphql-codegen";
import _, { map } from "lodash";
import { useCallback, useMemo } from "react";
import { Permission } from "shared/auth/permissions";
import { JobStatusFilter } from "shared/jobs/types";
import useQuery from "utils/useQuery";

const FILTER_DATA = gql(`
query JobsFilterData($userMembershipIds: [String!]) {
    atsOffices: atssyncOffices {
      id
      name
    }
    atsDepartments: atssyncDepartments {
      id
      name
    }
    userMemberships(userMembershipIds: $userMembershipIds) {
      ...InterviewerForSelection
    }
  }
`);

const ALL_JOB_IDS = gql(`
query AllJobIds(
    $search: String
    $atsOfficeIds: [String!]
    $userMembershipIds: [String!]
    $atsDepartmentIds: [String!]
  ) {
    currentOrganization {
      id
      jobs(
        where: {
          isArchived: false
          search: $search
          atsOfficeIds: $atsOfficeIds
          atsDepartmentIds: $atsDepartmentIds
          userMembershipIds: $userMembershipIds
        }
      ) {
        id
      }
    }
  }
`);

const FETCH_JOBS = gql(`
query GetJobs(
    $search: String
    $atsOfficeIds: [String!]
    $atsDepartmentIds: [String!]
    $userMembershipIds: [String!]
    $offset: Int
    $limit: Int
    $orderBy: [JobOrderByInput!]
    $status: JobStatusFilter
  ) {
    currentUser {
      id
      currentOrganization {
        id
        atssyncAccounts {
          id
        }
      }
    }
    openJobs(
      where: {
        isArchived: false
        search: $search
        atsOfficeIds: $atsOfficeIds
        atsDepartmentIds: $atsDepartmentIds
        userMembershipIds: $userMembershipIds
        status: $status
      }
      offset: $offset
      limit: $limit
      orderBy: $orderBy
    ) {
      id
      name: internalName
      atsJob: atssyncJob {
        id
        name
        status
        requisitionId: code
        atsDepartments: departments {
          id
          name
        }
        atsOffices: offices {
          id
          name  
        }
        account {
          id
          type
        }
      }
      updatedAt
    }
    openJobsAggregate(
      where: {
        isArchived: false
        search: $search
        atsOfficeIds: $atsOfficeIds
        atsDepartmentIds: $atsDepartmentIds
        userMembershipIds: $userMembershipIds
        status: $status
      }
    ) {
      count
    }
  }
`);

function JobsPage() {
  const {
    pageIndex,
    pageSize,
    filters,
    globalFilter: search,
    sortBy,
  } = useCurrentQueryParamStateWithDefaults();
  const initialVariables = {
    limit: pageSize,
    offset: pageIndex * pageSize,
    search,
    atsDepartmentIds: _.find(filters, { id: "department" })?.value ?? [],
    atsOfficeIds: _.find(filters, { id: "office" })?.value ?? [],
    userMembershipIds: _.find(filters, { id: "userMembership" })?.value ?? [],
    orderBy: _(sortBy)
      .map(({ id, desc }) => ({ [id]: desc ? "desc" : "asc" }))
      .value(),
    status: _.find(filters, { id: "status" })?.value ?? JobStatusFilter.OPEN,
  };
  const { data: filterData, loading: filtersLoading } = useQuery(FILTER_DATA, {
    variables: {
      userMembershipIds: initialVariables.userMembershipIds,
    },
  });

  const queryVariables = {
    ...initialVariables,
  };

  const { data, loading: isLoadingJobs } = useQuery(FETCH_JOBS, {
    variables: queryVariables,
    fetchPolicy: "cache-and-network",
  });

  const { data: allJobIdsData, refetch: refetchAllIds } = useQuery(
    ALL_JOB_IDS,
    {
      variables: queryVariables,
      fetchPolicy: "cache-and-network",
    }
  );

  const jobs = data?.openJobs ?? [];
  const selectedUserMemberships =
    filterData?.userMemberships.filter((u) =>
      initialVariables.userMembershipIds.includes(u.id)
    ) ?? [];

  const atsDepartments = useMemo(
    () =>
      _.map(filterData?.atsDepartments, ({ id, name }) => ({
        id,
        label: name,
      })),
    [filterData?.atsDepartments]
  );
  const atsOffices = useMemo(
    () =>
      _.map(filterData?.atsOffices, ({ id, name }) => ({ id, label: name })),
    [filterData?.atsOffices]
  );
  const totalCount = data?.openJobsAggregate.count;

  const allJobIds = map(allJobIdsData?.currentOrganization?.jobs, "id");

  const refetchIds = useCallback(
    async ({ search: newSearch }: { search: string }) => {
      refetchAllIds({
        search: newSearch,
      });
    },
    [refetchAllIds]
  );

  return (
    <div className="space-y-6">
      <h1 className="text-display-2">Jobs</h1>
      <JobsTable
        initialVariables={initialVariables}
        loading={isLoadingJobs}
        filtersLoading={filtersLoading}
        jobs={jobs}
        allJobIds={allJobIds}
        refetchIds={refetchIds}
        offices={atsOffices}
        departments={atsDepartments}
        totalJobsCount={totalCount}
        selectedUserMemberships={selectedUserMemberships}
      />
    </div>
  );
}

export default withPageAuthRequired(JobsPage, {
  requiredPermissions: Permission.JOB.WRITE,
  getLayout: (page) => (
    <ModalProvider>
      <Layout>{page}</Layout>
    </ModalProvider>
  ),
});
